import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabaseClient } from "@/lib/supabase";

interface UpdateAllRatingListKeysParams {
    trackerId: string;
    inputId: string;
    oldKey: string;
    newKey: string;
}

export async function updateAllRatingListKeys({ trackerId, inputId, oldKey, newKey }: UpdateAllRatingListKeysParams) {
    console.log("🔄 Starting updateAllRatingListKeys:", {
        trackerId,
        inputId,
        oldKey,
        newKey,
    });

    // First, get all entries for this tracker
    const { data: entries, error: fetchError } = await supabaseClient
        .from("entries_view")
        .select("*, entry_values_view(*)")
        .eq("tracker_id", trackerId);

    if (fetchError) {
        console.error("❌ Error fetching entries:", fetchError);
        throw fetchError;
    }

    console.log("📊 Found entries:", entries?.length || 0);

    if (!entries || entries.length === 0) {
        console.log("⚠️ No entries found for tracker");
        return { updated: 0 };
    }

    // Find all entry values for the specific input
    const entryValuesToUpdate = [];
    for (const entry of entries) {
        if (entry.entry_values_view) {
            const entryValue = entry.entry_values_view.find(
                (ev: any) => ev.input_id === inputId && ev.input_key === "ratings-list"
            );
            if (entryValue) {
                entryValuesToUpdate.push(entryValue);
            }
        }
    }

    console.log("🎯 Found entry values to update:", entryValuesToUpdate.length);

    if (entryValuesToUpdate.length === 0) {
        console.log("⚠️ No entry values found for this input");
        return { updated: 0 };
    }

    // Update each entry value that contains the old key
    let updatedCount = 0;
    const updatePromises = entryValuesToUpdate.map(async (entryValue) => {
        if (entryValue.json_value && typeof entryValue.json_value === "object") {
            const jsonValue = entryValue.json_value as Record<string, number>;
            console.log("🔍 Checking entry value:", {
                entryValueId: entryValue.entry_value_id,
                jsonValue,
                oldKey,
            });

            // Check if this entry value contains the old key
            if (oldKey in jsonValue) {
                console.log("✅ Found old key, updating...");
                // Create new json_value with updated key
                const newJsonValue = { ...jsonValue };
                const value = newJsonValue[oldKey];
                delete newJsonValue[oldKey];
                newJsonValue[newKey] = value as number;

                console.log("🔄 Updating entry value:", {
                    entryValueId: entryValue.entry_value_id,
                    oldJsonValue: jsonValue,
                    newJsonValue,
                });

                // Update the entry value
                const { error: updateError } = await supabaseClient
                    .from("entry_values")
                    .update({
                        json_value: newJsonValue,
                        last_edited: new Date().toISOString(),
                    })
                    .eq("entry_value_id", entryValue.entry_value_id);

                if (updateError) {
                    console.error("❌ Error updating entry value:", updateError);
                    throw updateError;
                }
                updatedCount++;
                console.log("✅ Successfully updated entry value");
            } else {
                console.log("⚠️ Old key not found in this entry value");
            }
        }
    });

    await Promise.all(updatePromises);

    console.log("🎉 Update complete. Updated count:", updatedCount);
    return { updated: updatedCount };
}

export const useUpdateAllRatingListKeys = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateAllRatingListKeys,
        onSuccess: (data, variables) => {
            // Invalidate relevant queries to refresh the UI
            queryClient.invalidateQueries({
                queryKey: ["entry_values", variables.inputId, variables.trackerId],
            });

            // Also invalidate any entry-related queries
            queryClient.invalidateQueries({
                queryKey: ["entries"],
            });

            // Invalidate tracker queries
            queryClient.invalidateQueries({
                queryKey: ["trackers"],
            });

            console.log(
                `✅ Updated ${data.updated} entry values with key change: ${variables.oldKey} → ${variables.newKey}`
            );
        },
        onError: (error, variables) => {
            console.error("❌ Failed to update rating list keys:", error);
            console.error("Parameters:", variables);
        },
    });
};
